/**
 * Where this browser keeps its trusted-computer keys (v3.91.0).
 *
 * IndexedDB, because it is the only browser store that can hold a WebCrypto
 * key object: the key is saved as a non-extractable CryptoKey, so even code
 * running on the page can use it but not read the secret. One record per
 * enrolled factor:
 *
 *   factorUid   the Firebase MFA enrollment id (key path)
 *   userUid     the account it belongs to
 *   key         CryptoKey (HMAC, non-extractable, sign only)
 *   digits, periodSeconds
 *   createdAt, expiresAt   ms since epoch (this device's clock)
 *
 * No email, no name, no student data. Clearing the site's data in the browser
 * removes the records — the computer then asks for a code again. The factor
 * it leaves behind on the account is removed at a later sign-in with a code
 * once it is past its date and no longer the account's newest factor, or by
 * hand in Security Settings (the app says so when one is waiting).
 *
 * Every function resolves even when IndexedDB is missing or fails: a broken
 * store means "no trusted computer", never a broken sign-in.
 */

const DB_NAME = 'literacy-app-trusted-computer';
const DB_VERSION = 1;
const STORE = 'factors';

function idb() {
  try {
    return typeof window !== 'undefined' && window.indexedDB ? window.indexedDB : null;
  } catch (err) {
    return null; // some privacy modes throw on access
  }
}

export function storeAvailable() {
  return !!idb();
}

function open() {
  return new Promise((resolve, reject) => {
    const factory = idb();
    if (!factory) { reject(new Error('IndexedDB is not available.')); return; }
    const req = factory.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'factorUid' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed.'));
    req.onblocked = () => reject(new Error('IndexedDB open blocked.'));
  });
}

function run(mode, work) {
  return open().then((db) => new Promise((resolve, reject) => {
    let result;
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const req = work(store);
    if (req) req.onsuccess = () => { result = req.result; };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error || new Error('IndexedDB transaction failed.')); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('IndexedDB transaction aborted.')); };
  }));
}

/** Every record on this browser; [] when there are none or the store cannot be read. */
export async function getAllRecords() {
  try {
    const rows = await run('readonly', (store) => store.getAll());
    return Array.isArray(rows) ? rows.filter((r) => r && typeof r.factorUid === 'string') : [];
  } catch (err) {
    console.warn('Trusted computer: records could not be read.', err);
    return [];
  }
}

/** Saves one record. Rejects on failure — the caller must not leave an enrolled factor without its key. */
export async function putRecord(record) {
  await run('readwrite', (store) => store.put(record));
}

/** Removes one record; never rejects. */
export async function removeRecord(factorUid) {
  try {
    await run('readwrite', (store) => store.delete(factorUid));
  } catch (err) {
    console.warn('Trusted computer: record could not be removed.', err);
  }
}

export const indexedDbStore = { available: storeAvailable, getAll: getAllRecords, put: putRecord, remove: removeRecord };
